import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useOptimizedPresence } from '@/hooks/useOptimizedPresence';
import { ProductInterestsList } from '@/components/members/ProductInterestsList';
import { useProductInterests } from '@/hooks/useProductInterests';
import { useNetworkingProductionReady } from '@/hooks/useNetworkingProductionReady';
import { useConversations } from '@/hooks/useConversations';
import { useProfileViews } from '@/hooks/useProfileViews';
import { 
  MapPin, Briefcase, Languages, Award, Calendar, Star,
  MessageCircle, Users, Heart, Share2, ExternalLink,
  Clock, Activity, Eye, Zap, Brain, Network, Phone,
  Mail, Globe, Linkedin, Twitter, Github, Package, QrCode
} from 'lucide-react';
import { Member } from '@/types/member';
import { cn } from '@/lib/utils';
import { OtherMemberCardModal } from './OtherMemberCardModal';

interface MemberProfileModalProps {
  member: Member | null;
  isOpen: boolean;
  onClose: () => void;
  onMessage: (member: Member) => void;
  onConnect: (member: Member) => void;
  onFavorite: (member: Member) => void;
  onShare: (member: Member) => void;
  currentUserId?: string;
}

export const MemberProfileModal: React.FC<MemberProfileModalProps> = ({
  member,
  isOpen,
  onClose,
  onMessage,
  onConnect,
  onFavorite,
  onShare,
  currentUserId
}) => {
  const [isFavorited, setIsFavorited] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const { getPresence } = useOptimizedPresence();
  const { trackProfileView } = useProfileViews();

  // Product interests functionality
  const { data: productInterests = [], isLoading: interestsLoading } = useProductInterests(member?.id);
  
  // Networking and conversations data
  const {
    connections,
    connectionRequests,
    isLoading: networkingLoading,
    networkingStats,
    recentActivity
  } = useNetworkingProductionReady();
  
  const { conversations } = useConversations();

  // Check connection status for intelligent connect button (Phase 1 Security)
  const existingConnection = member ? connections.find(
    conn => {
      const connName = `${conn.firstname} ${conn.lastname}`.trim().toLowerCase();
      const memberName = member.name?.toLowerCase() || '';
      return connName === memberName;
    }
  ) : undefined;

  const pendingRequest = member ? connectionRequests.find(
    req => {
      const requesterName = req.requester_profile ? 
        `${req.requester_profile.firstname} ${req.requester_profile.lastname}`.trim().toLowerCase() : '';
      const requestedName = req.requested_profile ?
        `${req.requested_profile.firstname} ${req.requested_profile.lastname}`.trim().toLowerCase() : '';
      const memberName = member.name?.toLowerCase() || '';
      return (requesterName === memberName || requestedName === memberName) && req.status === 'pending';
    }
  ) : undefined;

  // Tracker la vue du profil quand le modal s'ouvre
  useEffect(() => {
    if (isOpen && member && currentUserId && member.user_id !== currentUserId && member.id !== currentUserId) {
      // Debounce de 500ms pour éviter les multiples calls
      const timer = setTimeout(() => {
        trackProfileView(member.id);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, member?.id, currentUserId, trackProfileView]);

  if (!member) return null;

  const isCurrentUser = currentUserId && (member.user_id === currentUserId || member.id === currentUserId);
  
  // Use real presence data
  const userPresence = getPresence(member.user_id);
  const isOnline = userPresence?.is_active || false;
  const lastActive = isOnline ? 'En ligne maintenant' : (
    userPresence?.last_seen ? 
    `Vu il y a ${new Date(Date.now() - new Date(userPresence.last_seen).getTime()).getMinutes()}min` : 
    'Hors ligne'
  );

  const handleFavorite = () => {
    setIsFavorited(!isFavorited);
    onFavorite(member);
  };

  const socialLinks = [
    { platform: 'LinkedIn', icon: Linkedin, url: member.linkedin, color: 'text-blue-600' },
    { platform: 'Twitter', icon: Twitter, url: member.twitter, color: 'text-blue-400' },
    { platform: 'Website', icon: Globe, url: member.website, color: 'text-green-600' },
  ].filter(link => link.url);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-0">
          <DialogTitle className="sr-only">Profil de {member.name}</DialogTitle>
        </DialogHeader>

        {/* Profile Header */}
        <div className="relative">
          {/* Cover Image */}
          <div className="h-32 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent rounded-lg">
            <div className="absolute top-4 right-4 flex items-center gap-2">
              {isOnline && (
                <Badge variant="default" className="bg-green-500/90 text-white">
                  <Activity className="h-3 w-3 mr-1" />
                  En ligne
                </Badge>
              )}
            </div>
          </div>

          {/* Profile Info */}
          <div className="relative -mt-16 px-6">
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="relative">
                <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
                  <AvatarImage src={member.image} alt={member.name} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-semibold">
                    {member.name?.charAt(0) || 'M'}
                  </AvatarFallback>
                </Avatar>
                <div className={cn(
                  "absolute bottom-1 right-1 h-6 w-6 rounded-full border-2 border-background",
                  isOnline ? "bg-green-500" : "bg-gray-400"
                )} />
              </div>

              <div className="flex-1 space-y-2">
                <div>
                  <h1 className="text-2xl font-bold text-foreground">{member.name}</h1>
                  <p className="text-lg text-muted-foreground">{member.role} chez {member.company}</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                    <MapPin className="h-4 w-4" />
                    <span>{member.country}</span>
                    <span>•</span>
                    <Clock className="h-4 w-4" />
                    <span>{lastActive}</span>
                  </div>
                </div>

              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleFavorite} title="Ajouter aux favoris">
                  <Heart className={cn("h-4 w-4", isFavorited && "fill-red-500 text-red-500")} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onShare(member)} title="Partager le profil">
                  <Share2 className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setIsQRModalOpen(true)} title="Voir la carte QR">
                  <QrCode className="h-4 w-4" />
                </Button>
                
                {!isCurrentUser && (
                  <>
                    <Button variant="outline" onClick={() => onMessage(member)}>
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Message
                    </Button>
                    
                    {existingConnection ? (
                      <Badge variant="secondary" className="px-3 py-2">
                        <Users className="h-4 w-4 mr-2" />
                        Déjà connecté
                      </Badge>
                    ) : pendingRequest ? (
                      <Badge variant="outline" className="px-3 py-2">
                        <Clock className="h-4 w-4 mr-2" />
                        Demande en cours
                      </Badge>
                    ) : (
                      <Button variant="default" onClick={() => onConnect(member)}>
                        <Users className="h-4 w-4 mr-2" />
                        Se connecter
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Profile Content */}
        <div className="px-6 pb-6 space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className={cn("grid w-full", isCurrentUser ? "grid-cols-5" : "grid-cols-4")}>
              <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
              <TabsTrigger value="experience">Expérience</TabsTrigger>
              <TabsTrigger value="proposals">Attentes & Propositions</TabsTrigger>
              <TabsTrigger value="network">Réseau</TabsTrigger>
              {isCurrentUser && <TabsTrigger value="activity">Activité</TabsTrigger>}
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Bio */}
                {member.bio && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">À propos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {member.bio}
                      </p>
                    </CardContent>
                  </Card>
                )}

                 {/* Contact Info */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Contact</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {member.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{member.phone}</span>
                      </div>
                    )}

                    {socialLinks.length > 0 && (
                      <div className="space-y-2">
                        <span className="text-sm font-medium">Réseaux sociaux</span>
                        <div className="flex items-center gap-3">
                          {socialLinks.map(({ platform, icon: Icon, url, color }) => (
                            <Button key={platform} variant="ghost" size="sm" asChild>
                              <a href={url} target="_blank" rel="noopener noreferrer">
                                <Icon className={cn("h-4 w-4", color)} />
                              </a>
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Skills & Languages */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {member.skills && member.skills.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Zap className="h-5 w-5 text-primary" />
                        Compétences
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {member.skills.map((skill, index) => (
                          <Badge key={index} variant="default" className="bg-primary/10 text-primary">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {member.languages && member.languages.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Languages className="h-5 w-5 text-primary" />
                        Langues
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {member.languages.map((language, index) => (
                          <Badge key={index} variant="secondary">
                            {language}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Achievements & Needs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {member.achievements && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Award className="h-5 w-5 text-primary" />
                        Réalisations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {member.achievements}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {member.needs && member.needs.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Star className="h-5 w-5 text-primary" />
                        Recherche
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {member.needs.map((need, index) => (
                          <Badge key={index} variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                            {need}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="experience" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-primary" />
                    Expérience Professionnelle
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="border-l-2 border-primary/20 pl-4 space-y-4">
                    <div>
                      <h4 className="font-semibold">{member.role}</h4>
                      <p className="text-sm text-muted-foreground">{member.company}</p>
                      <p className="text-xs text-muted-foreground">2022 - Présent</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {member.tags && member.tags.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Secteurs d'activité</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {member.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="proposals" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Ce que je recherche */}
                {member.needs && member.needs.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Star className="h-5 w-5 text-amber-500" />
                        Ce que je recherche
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {member.needs.map((need, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <div className="h-2 w-2 rounded-full bg-amber-500 mt-2" />
                            <p className="text-sm">{need}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Ce que je propose */}
                {member.offers && member.offers.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Brain className="h-5 w-5 text-green-500" />
                        Ce que je propose
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {member.offers.map((offer, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <div className="h-2 w-2 rounded-full bg-green-500 mt-2" />
                            <p className="text-sm">{offer}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Si aucune donnée */}
                {(!member.needs || member.needs.length === 0) && (!member.offers || member.offers.length === 0) && (
                  <Card className="col-span-2">
                    <CardContent className="text-center py-8">
                      <Brain className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">Aucune information renseignée</h3>
                      <p className="text-muted-foreground">
                        {isCurrentUser 
                          ? "Complétez votre profil pour indiquer ce que vous recherchez et ce que vous proposez."
                          : `${member.name} n'a pas encore renseigné ses attentes et propositions.`
                        }
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="network" className="space-y-6">
              {isCurrentUser ? (
                <>
                  {/* Full Network Statistics - Owner Only */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Connexions</p>
                            <p className="text-2xl font-bold">{networkingStats.totalConnections}</p>
                          </div>
                          <Users className="h-8 w-8 text-primary" />
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Messages échangés</p>
                            <p className="text-2xl font-bold">{networkingStats.totalMessages}</p>
                          </div>
                          <MessageCircle className="h-8 w-8 text-primary" />
                        </div>
                      </CardContent>
                    </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Demandes en attente</p>
                        <p className="text-2xl font-bold">{networkingStats.pendingRequests}</p>
                      </div>
                      <Clock className="h-8 w-8 text-primary" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Connections */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    Connexions actives ({connections.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {networkingLoading ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Chargement du réseau...
                    </div>
                  ) : connections.length > 0 ? (
                    <div className="space-y-3">
                      {connections.slice(0, 6).map((connection) => (
                        <div key={connection.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarImage src={connection.avatar_url} />
                              <AvatarFallback>{connection.firstname?.charAt(0) || connection.lastname?.charAt(0) || 'U'}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{`${connection.firstname || ''} ${connection.lastname || ''}`.trim()}</p>
                              <p className="text-sm text-muted-foreground">{connection.company}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">
                              Connecté
                            </Badge>
                            <Button variant="ghost" size="sm">
                              <MessageCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {connections.length > 6 && (
                        <p className="text-center text-sm text-muted-foreground">
                          et {connections.length - 6} autres connexions...
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">Aucune connexion</h3>
                      <p className="text-muted-foreground">
                        Commencez à développer votre réseau professionnel.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Connection Requests */}
              {connectionRequests.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-primary" />
                      Demandes de connexion ({connectionRequests.filter(req => req.status === 'pending').length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {connectionRequests
                        .filter(req => req.status === 'pending')
                        .slice(0, 3)
                        .map((request) => (
                        <div key={request.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback>
                                {request.requester_profile?.firstname?.charAt(0) || 
                                 request.requested_profile?.firstname?.charAt(0) || 'U'}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">
                                {request.requester_profile ? 
                                  `${request.requester_profile.firstname} ${request.requester_profile.lastname}`.trim() :
                                  request.requested_profile ?
                                  `${request.requested_profile.firstname} ${request.requested_profile.lastname}`.trim() :
                                  'Utilisateur'
                                }
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {request.message || 'Souhaite se connecter'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm">
                              Accepter
                            </Button>
                            <Button variant="ghost" size="sm">
                              Refuser
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
                </>
              ) : (
                <>
                  {/* Limited Network View for Visitors */}
                  <Card>
                    <CardContent className="text-center py-12">
                      <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">
                        {networkingStats.totalConnections} connexions
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        Les détails du réseau sont privés
                      </p>
                      {existingConnection && (
                        <Badge variant="secondary" className="text-sm">
                          <Users className="h-4 w-4 mr-2" />
                          Vous êtes connectés
                        </Badge>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>

            {isCurrentUser && (
              <TabsContent value="activity" className="space-y-6">
              {/* Recent Messages */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageCircle className="h-5 w-5 text-primary" />
                    Messages récents
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {conversations.length > 0 ? (
                    <div className="space-y-3">
                      {conversations.slice(0, 5).map((conversation) => (
                        <div key={conversation.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarImage src={conversation.participantAvatar} />
                              <AvatarFallback>{conversation.participantName?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <p className="font-medium">{conversation.participantName}</p>
                              <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                                {conversation.lastMessage.content}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(conversation.lastMessage.timestamp).toLocaleDateString('fr-FR', {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {conversation.unreadCount > 0 && (
                              <Badge variant="default" className="bg-primary text-primary-foreground">
                                {conversation.unreadCount}
                              </Badge>
                            )}
                            <Button variant="ghost" size="sm">
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">Aucun message récent</h3>
                      <p className="text-muted-foreground">
                        Commencez une conversation pour voir l'activité ici.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Connections */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    Connexions récentes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {recentActivity?.connections?.length > 0 ? (
                    <div className="space-y-3">
                      {recentActivity.connections.map((connection, index) => (
                        <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarImage src={connection.avatar_url} />
                              <AvatarFallback>{connection.firstname?.charAt(0) || connection.lastname?.charAt(0) || 'U'}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{`${connection.firstname || ''} ${connection.lastname || ''}`.trim()}</p>
                              <p className="text-sm text-muted-foreground">{connection.company}</p>
                              <p className="text-xs text-muted-foreground">
                                Connecté récemment
                              </p>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">Aucune connexion récente</h3>
                      <p className="text-muted-foreground">
                        Les nouvelles connexions apparaîtront ici.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Activity Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-primary" />
                    Timeline d'activité
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {recentActivity?.messages?.length > 0 ? (
                    <div className="space-y-4">
                      {recentActivity.messages.map((message, index) => (
                        <div key={index} className="flex items-start gap-3 p-3 border-l-2 border-primary/20 pl-4">
                          <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <MessageCircle className="h-4 w-4 text-primary" />
                              <span className="text-sm font-medium">Message envoyé</span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(message.created_at).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {message.content}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">Aucune activité récente</h3>
                      <p className="text-muted-foreground">
                        L'activité de networking apparaîtra ici.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
              </TabsContent>
            )}

          </Tabs>
        </div>

        {/* QR Code Modal */}
        <OtherMemberCardModal 
          member={member}
          open={isQRModalOpen}
          onOpenChange={setIsQRModalOpen}
        />
      </DialogContent>
    </Dialog>
  );
};